<template>
  <ReportLayout @period-changed="onPeriodChanged" :back-page="{ name: 'CenterReports', params: { centerId, id: srId } }">
    <div class="my-3">
      <Transition name="grow">
        <div v-if="showFamilies" class="flex flex-wrap">
          <FamilyBadge @click="badge.visible = !badge.visible" class="m-1"
            v-for="badge in centerReportStore.familiesSortedByCurrentTotal"
            :label="badge.label" :color="badge.color" :active="badge.visible" />
        </div>
      </Transition>
    </div>

    <div class="grid gap-5">
      <!-- Graphique principal des ventes -->
      <SalesCard :title="$t('report.sales_comparison.sales_history')"
        :show-chart="true" :center="centerId" :component="srId"
        :cumulated="false" :show-evolution="false"
        :hide-edit="true" :x-axis-formatter="formatXLabel"
        :seasonality-data="seasonalityData">
        <template #toolbar>
          <div class="flex justify-center">
            <SegmentedPicker
              v-model="aggregation"
              :groups="pickerGroups"
            />
          </div>
        </template>
      </SalesCard>

      <!-- Top 5 des produits -->
      <ReportCard v-if="productsReport" :title="$t('center_sales_report.top_performances')">
        <Top5Products :products-report="productsReport" :center="center" />
      </ReportCard>

      <!-- Analyse de concentration -->
      <ReportCard v-if="productsReport && version > '1.4.2'" :title="$t('center_sales_report.risks_analysis')">
        <TrendingInsights :products-report="productsReport" :center="center" />
      </ReportCard>
    </div>
  </ReportLayout>
</template>
<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useCenterReportStore } from '../../../stores/centerReportStore'
import { useSalesReportsStore } from '../../../stores/salesReports'
import SalesCard from '../../../components/SalesCard.vue'
import ReportCard from '../../../components/ReportCard.vue'
import Top5Products from '../../../components/Top5Products.vue'
import TrendingInsights from '../../../components/TrendingInsights.vue'
import FamilyBadge from '../../../components/FamilyBadge.vue'
import ReportLayout from '../../../layouts/ReportLayout.vue'
import SegmentedPicker, { type PickerGroup } from '../../../components/ui/SegmentedPicker.vue'
import { useTimeAggregation } from '../../../composables/useTimeAggregation'
import packageJson from '../../../../package.json';
import { useAppSettingsStore } from '@/stores/AppSettingsStore';
import { Center } from '@/user';
import { SalesApi } from '@/api/SalesApi';

const { t } = useI18n();
const version = packageJson.version;
const router = useRouter();

const centerReportStore = useCenterReportStore();
const salesReportsStore = useSalesReportsStore();
const appSettingsStore = useAppSettingsStore();

const centerId = parseInt(router.currentRoute.value.params.centerId as string);
const srId = router.currentRoute.value.params.id as string;

const card = computed(() => {
  return salesReportsStore.getReportForCenterAndComponent(centerId, srId);
})

const showFamilies = ref(false);

const productsReport = computed(() => {
  return card.value?.productsReport || null;
})

const center = computed<Center>(() => {
  let c = appSettingsStore.getCenter(centerId);
  if (!c) {
    throw new Error("CenterSalesReport: Should not end up here without center loaded")
  }
  return c
})

const seasonalityData = ref<number[] | undefined>(undefined);

// On reload, the store is populated by the beforeEach guard but fetchProductsReport
// is never called (that normally happens via Center.vue). Watch for the card becoming
// available and trigger the fetch if productsReport is missing.
watch(card, async (newCard) => {
  if (newCard && !newCard.productsReport) {
    if (!newCard.centerId) {
      newCard.centerId = centerId;
    }
    await newCard.fetchProductsReport();
  }
}, { immediate: true });

async function fetchSeasonalityData(intervalOverride?: string) {
  if (!card.value) return;
  try {
    const deposit = card.value.dataType === 'deposit';
    const n1Start = card.value.date.startDate.minus({ years: 1 });
    const n1End = card.value.date.endDate.minus({ years: 1 });
    const interval = (intervalOverride ?? card.value.interval) as any;

    const resp = await SalesApi.fetchSalesByInterval(
      n1Start, n1End,
      interval, card.value.dataOrigin, deposit, centerId
    );

    const taxKey = card.value.getTaxKey();
    seasonalityData.value = resp.intervals.map(
      i => i.data.cumulative.tax_data[taxKey]
    );
  } catch {
    seasonalityData.value = undefined;
  }
}

// --- Time aggregation ---
const startDate = computed(() => card.value?.date?.startDate?.toJSDate() ?? new Date());
const endDate = computed(() => card.value?.date?.endDate?.toJSDate() ?? new Date());
const { aggregation, aggregationOptions, formatXLabel } = useTimeAggregation(startDate, endDate);

const pickerGroups = computed<PickerGroup[]>(() => {
  const chronological = aggregationOptions.value.filter(o => o.group === 'chronological');
  const distribution = aggregationOptions.value.filter(o => o.group === 'distribution');
  const groups: PickerGroup[] = [];
  if (chronological.length) {
    groups.push({
      label: t('time_aggregation.group_chronological'),
      options: chronological.map(o => ({ value: o.value, label: o.label })),
    });
  }
  if (distribution.length) {
    groups.push({
      label: t('time_aggregation.group_distribution'),
      options: distribution.map(o => ({ value: o.value, label: o.label })),
    });
  }
  return groups;
});

// Restore aggregation from model if returning to this page
const restoring = ref(false);
if (card.value?.salesAggregation) {
  restoring.value = true;
  aggregation.value = card.value.salesAggregation as typeof aggregation.value;
  // nextTick would be ideal but we set restoring=false in the watcher guard
  setTimeout(() => { restoring.value = false; }, 0);
}

watch(aggregation, async (newAgg) => {
  if (!card.value || restoring.value) return;
  card.value.data = null;
  try {
    await Promise.all([
      card.value.fetchData(centerId, true, newAgg),
      fetchSeasonalityData(newAgg),
    ]);
  } catch {
    // errors handled inside fetchData
  }
});

const onPeriodChanged = async () => {
  if (!card.value) return
  card.value.data = null
  card.value.productsReport = null
  seasonalityData.value = undefined;

  try {
    await Promise.all([
      card.value.fetchData(centerId, true, aggregation.value),
      card.value.fetchProductsReport(),
      fetchSeasonalityData(aggregation.value),
    ])
  } catch {
    // errors handled inside fetch methods
  }
}

onBeforeUnmount(() => {
  if (!card.value) return;
  // If aggregation differs from the card's default interval, clear cached data
  // so the dashboard re-fetches with the default interval
  if (aggregation.value !== card.value.interval) {
    card.value.data = null;
    card.value.salesAggregation = null;
  }
});
</script>
